<script setup>
import { ref } from 'vue';

const showGuide = ref(false);

const toggleGuide = () => {
  showGuide.value = !showGuide.value;
};

const examples = [
  {
    id: 1,
    title: 'Ejemplo GET: Consulta de Pedido',
    method: 'GET',
    description: 'Consultar el estado de un pedido usando su ID',
    config: {
      name: 'Búsqueda de Pedido',
      description: 'Busca detalles del pedido por ID de pedido',
      method: 'GET',
      url: 'https://api.tutienda.com/orders/ORDER_ID',
      authType: 'Bearer Token',
      authConfig: 'tu_token_secreto_aqui',
      parameters: [
        {
          name: 'order_id',
          type: 'String',
          description: 'ID del pedido del cliente',
          required: true,
        },
      ],
      responseTemplate: 'Pedido ORDER_ID - Estado: STATUS - Total: TOTAL',
    },
  },
  {
    id: 2,
    title: 'Ejemplo POST: Crear Ticket de Soporte',
    method: 'POST',
    description: 'Crear un ticket en sistema externo de soporte',
    config: {
      name: 'Crear Ticket',
      description: 'Crea un ticket de soporte en el sistema externo',
      method: 'POST',
      url: 'https://api.soporte.com/tickets',
      authType: 'API Key',
      authConfig: {
        headerName: 'X-API-Key',
        headerValue: 'tu_api_key_aqui',
      },
      parameters: [
        {
          name: 'subject',
          type: 'String',
          description: 'Asunto del ticket',
          required: true,
        },
        {
          name: 'description',
          type: 'String',
          description: 'Descripción del problema',
          required: true,
        },
        {
          name: 'priority',
          type: 'String',
          description: 'Prioridad del ticket (low, medium, high)',
          required: false,
        },
      ],
      requestTemplate:
        "{ 'subject': 'SUBJECT', 'description': 'DESCRIPTION', 'priority': 'PRIORITY' }",
      responseTemplate: 'Ticket creado: #TICKET_ID - Estado: STATUS',
    },
  },
  {
    id: 3,
    title: 'Ejemplo GET: Verificar Inventario',
    method: 'GET',
    description: 'Consultar disponibilidad de producto en inventario',
    config: {
      name: 'Verificar Inventario',
      description: 'Verifica la disponibilidad de un producto en el inventario',
      method: 'GET',
      url: 'https://api.tutienda.com/inventory/PRODUCT_SKU',
      authType: 'API Key',
      authConfig: {
        headerName: 'X-API-Key',
        headerValue: 'tu_api_key_aqui',
      },
      parameters: [
        {
          name: 'product_sku',
          type: 'String',
          description: 'SKU del producto',
          required: true,
        },
      ],
      responseTemplate:
        'Producto PRODUCT_SKU - Stock: QUANTITY unidades - Precio: PRICE',
    },
  },
  {
    id: 4,
    title: 'Ejemplo POST: Actualizar Estado de Cliente',
    method: 'POST',
    description: 'Actualizar información del cliente en CRM',
    config: {
      name: 'Actualizar Cliente',
      description: 'Actualiza el estado o información del cliente en el CRM',
      method: 'POST',
      url: 'https://api.crm.com/customers/CUSTOMER_ID',
      authType: 'Bearer Token',
      authConfig: 'tu_bearer_token_aqui',
      parameters: [
        {
          name: 'customer_id',
          type: 'String',
          description: 'ID del cliente',
          required: true,
        },
        {
          name: 'status',
          type: 'String',
          description: 'Nuevo estado del cliente',
          required: true,
        },
        {
          name: 'notes',
          type: 'String',
          description: 'Notas adicionales',
          required: false,
        },
      ],
      requestTemplate:
        "{ 'status': 'STATUS', 'notes': 'NOTES', 'updated_at': 'TIMESTAMP' }",
      responseTemplate:
        'Cliente CUSTOMER_ID actualizado - Nuevo estado: STATUS',
    },
  },
];
</script>

<template>
  <div class="bg-n-slate-2 border border-n-slate-6 rounded-lg p-4 mb-6">
    <button
      class="flex items-center justify-between w-full text-left"
      @click="toggleGuide"
    >
      <div class="flex items-center gap-2">
        <i class="i-lucide-book-open text-n-blue-11 text-lg" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          Guía de Herramientas Personalizadas
        </h3>
      </div>
      <i
        :class="[showGuide ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down']"
        class="text-n-slate-11 transition-transform duration-200"
      />
    </button>

    <div v-if="showGuide" class="mt-4 space-y-4 border-t border-n-slate-6 pt-4">
      <!-- Introducción -->
      <div class="prose prose-sm max-w-none">
        <p class="text-sm text-n-slate-11">
          Las <strong>Herramientas Personalizadas</strong> permiten que tu
          asistente de Captain AI se conecte con APIs externas para consultar
          información, crear registros o actualizar datos en tiempo real.
        </p>
      </div>

      <!-- Herramientas Integradas -->
      <div class="bg-n-slate-3 rounded-md p-3">
        <h4
          class="text-xs font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
        >
          <i class="i-lucide-wrench text-n-blue-11" />
          Herramientas Integradas Disponibles
        </h4>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="flex items-start gap-1.5">
            <i class="i-lucide-note-add text-n-slate-10 mt-0.5" />
            <div>
              <code class="text-n-blue-11">add_contact_note</code>
              <p class="text-n-slate-10 text-[11px]">
                Agregar nota al contacto
              </p>
            </div>
          </div>
          <div class="flex items-start gap-1.5">
            <i class="i-lucide-eye-off text-n-slate-10 mt-0.5" />
            <div>
              <code class="text-n-blue-11">add_private_note</code>
              <p class="text-n-slate-10 text-[11px]">Agregar nota privada</p>
            </div>
          </div>
          <div class="flex items-start gap-1.5">
            <i class="i-lucide-alert-triangle text-n-slate-10 mt-0.5" />
            <div>
              <code class="text-n-blue-11">update_priority</code>
              <p class="text-n-slate-10 text-[11px]">Actualizar prioridad</p>
            </div>
          </div>
          <div class="flex items-start gap-1.5">
            <i class="i-lucide-tag text-n-slate-10 mt-0.5" />
            <div>
              <code class="text-n-blue-11">add_label_to_conversation</code>
              <p class="text-n-slate-10 text-[11px]">Agregar etiqueta</p>
            </div>
          </div>
          <div class="flex items-start gap-1.5">
            <i class="i-lucide-search text-n-slate-10 mt-0.5" />
            <div>
              <code class="text-n-blue-11">faq_lookup</code>
              <p class="text-n-slate-10 text-[11px]">Buscar en FAQs</p>
            </div>
          </div>
          <div class="flex items-start gap-1.5">
            <i class="i-lucide-check text-n-slate-10 mt-0.5" />
            <div>
              <code class="text-n-blue-11">resolve_conversation</code>
              <p class="text-n-slate-10 text-[11px]">Resolver conversación</p>
            </div>
          </div>
          <div class="flex items-start gap-1.5 col-span-2">
            <i class="i-lucide-user-switch text-n-slate-10 mt-0.5" />
            <div>
              <code class="text-n-blue-11">handoff</code>
              <p class="text-n-slate-10 text-[11px]">Transferir a humano</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Ejemplos -->
      <div>
        <h4
          class="text-xs font-semibold text-n-slate-12 mb-3 flex items-center gap-2"
        >
          <i class="i-lucide-code-2 text-n-blue-11" />
          Ejemplos de Configuración
        </h4>
        <div class="space-y-3">
          <div
            v-for="example in examples"
            :key="example.id"
            class="bg-n-slate-3 rounded-md p-3 border border-n-slate-6"
          >
            <div class="flex items-start justify-between mb-2">
              <div>
                <h5 class="text-xs font-semibold text-n-slate-12">
                  {{ example.title }}
                </h5>
                <p class="text-[11px] text-n-slate-10 mt-0.5">
                  {{ example.description }}
                </p>
              </div>
              <span
                class="px-2 py-0.5 rounded text-[10px] font-mono font-semibold"
                :class="[
                  example.method === 'GET'
                    ? 'bg-n-green-3 text-n-green-11'
                    : 'bg-n-blue-3 text-n-blue-11',
                ]"
              >
                {{ example.method }}
              </span>
            </div>

            <div class="space-y-2 text-[11px]">
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <span class="text-n-slate-10">Nombre:</span>
                  <code class="text-n-slate-12 ml-1">{{
                    example.config.name
                  }}</code>
                </div>
                <div>
                  <span class="text-n-slate-10">Autenticación:</span>
                  <code class="text-n-slate-12 ml-1">{{
                    example.config.authType
                  }}</code>
                </div>
              </div>

              <div>
                <span class="text-n-slate-10">URL:</span>
                <code
                  class="block bg-n-slate-4 px-2 py-1 rounded mt-1 text-[10px] font-mono break-all"
                >
                  {{ example.config.url }}
                </code>
              </div>

              <div>
                <span class="text-n-slate-10">Parámetros:</span>
                <ul class="mt-1 space-y-1">
                  <li
                    v-for="param in example.config.parameters"
                    :key="param.name"
                    class="flex items-start gap-1.5 bg-n-slate-4 px-2 py-1 rounded"
                  >
                    <code class="text-n-blue-11 font-semibold">{{
                      param.name
                    }}</code>
                    <span class="text-n-slate-10">:</span>
                    <span class="text-n-slate-11">{{ param.type }}</span>
                    <span
                      v-if="param.required"
                      class="text-n-red-11 text-[10px]"
                      >*</span>
                    <span class="text-n-slate-10 text-[10px]">- {{ param.description }}</span>
                  </li>
                </ul>
              </div>

              <div v-if="example.config.requestTemplate">
                <span class="text-n-slate-10">Request Template:</span>
                <code
                  class="block bg-n-slate-4 px-2 py-1 rounded mt-1 text-[10px] font-mono break-all"
                >
                  {{ example.config.requestTemplate }}
                </code>
              </div>

              <div>
                <span class="text-n-slate-10">Response Template:</span>
                <code
                  class="block bg-n-slate-4 px-2 py-1 rounded mt-1 text-[10px] font-mono break-all"
                >
                  {{ example.config.responseTemplate }}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Uso en Escenarios -->
      <div class="bg-n-blue-3 rounded-md p-3 border border-n-blue-6">
        <h4
          class="text-xs font-semibold text-n-blue-12 mb-2 flex items-center gap-2"
        >
          <i class="i-lucide-lightbulb text-n-blue-11" />
          Cómo Usar en Escenarios
        </h4>
        <p class="text-[11px] text-n-blue-11 mb-2">
          Una vez creada tu herramienta, úsala en escenarios con esta sintaxis:
        </p>
        <code
          class="block bg-n-blue-4 px-2 py-1.5 rounded text-[10px] font-mono text-n-blue-12"
        >
          [Nombre de la Herramienta](tool://id_de_la_herramienta)
        </code>
        <p class="text-[11px] text-n-blue-11 mt-2">
          <strong>Ejemplo:</strong> "Usa [Búsqueda de
          Pedido](tool://busqueda_pedido) para obtener el estado"
        </p>
      </div>
    </div>
  </div>
</template>
